import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { hasCrossAppDndType } from '@inno-tools/container-bridge'
import { ArrowUpRectangleLineIcon, IconSizes } from '@inno-ui/icons'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { useUploadFiles } from '../../hooks/upload'
import styles from './FileDropzone.module.scss'

const springTransition = {
  type: 'spring',
  stiffness: 400,
  damping: 32
} as const

const dropzoneVariants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: { opacity: 1, scale: 1 }
}

const INTERNAL_DESKTOP_DRAG_MIME =
  'application/x-container-desktop-internal-drag'

export const FileDropzone: FC = () => {
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)
  const isInternalDesktopDragRef = useRef(false)
  const uploadFiles = useUploadFiles()
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.file_upload'
  })

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isInternalDesktopDragRef.current) {
      return
    }

    if (hasCrossAppDndType(e.dataTransfer)) {
      return
    }

    if (e.dataTransfer?.types.includes(INTERNAL_DESKTOP_DRAG_MIME)) {
      return
    }

    if (e.dataTransfer?.effectAllowed === 'copyMove') {
      return
    }
    if (e.dataTransfer?.types.includes('Files')) {
      dragCounterRef.current += 1
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragStart = useCallback((e: DragEvent) => {
    const target = e.target as HTMLElement | null
    isInternalDesktopDragRef.current = Boolean(
      target?.closest('[data-desktop-internal-drag-source]')
    )
  }, [])

  const handleDragEnd = useCallback(() => {
    isInternalDesktopDragRef.current = false
    dragCounterRef.current = 0
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (isInternalDesktopDragRef.current) {
        handleDragEnd()
        return
      }

      if (hasCrossAppDndType(e.dataTransfer)) {
        return
      }

      if (e.dataTransfer?.types.includes(INTERNAL_DESKTOP_DRAG_MIME)) {
        return
      }

      if (e.dataTransfer?.effectAllowed === 'copyMove') {
        return
      }

      dragCounterRef.current = 0
      setIsDragging(false)

      const files = e.dataTransfer?.files
      if (files && files.length > 0) {
        const fileArray = Array.from(files)
        uploadFiles(fileArray)
      }
    },
    [handleDragEnd, uploadFiles]
  )

  useEffect(() => {
    document.addEventListener('dragstart', handleDragStart, true)
    document.addEventListener('dragend', handleDragEnd, true)
    document.addEventListener('dragenter', handleDragEnter)
    document.addEventListener('dragleave', handleDragLeave)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDrop)

    return () => {
      document.removeEventListener('dragstart', handleDragStart, true)
      document.removeEventListener('dragend', handleDragEnd, true)
      document.removeEventListener('dragenter', handleDragEnter)
      document.removeEventListener('dragleave', handleDragLeave)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('drop', handleDrop)
    }
  }, [
    handleDragStart,
    handleDragEnd,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop
  ])

  return (
    <AnimatePresence>
      {isDragging && (
        <motion.div
          className={styles.overlay}
          variants={dropzoneVariants}
          initial='hidden'
          animate='visible'
          exit='hidden'
          transition={springTransition}
        >
          <div className={styles.content}>
            <motion.div
              className={styles.iconWrapper}
              initial={{ y: 10 }}
              animate={{ y: 0 }}
              transition={springTransition}
            >
              <ArrowUpRectangleLineIcon
                color='var(--white)'
                size={IconSizes.Size48}
              />
            </motion.div>
            <motion.h2
              className={styles.title}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ...springTransition, delay: 0.05 }}
            >
              {t('drop_title')}
            </motion.h2>
            <motion.p
              className={styles.subtitle}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ...springTransition, delay: 0.1 }}
            >
              {t('drop_subtitle')}
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
